import { Button, Dialog, Flex, Text, TextField, IconButton } from '@radix-ui/themes';
import { AiOutlinePlus } from 'react-icons/ai';
import React, { useState } from 'react';
import axiosInstance from '../../config/axiosConfig';
import TableComponent from './TableComponent'; // Adjust path as needed

const AddSensorPopup = ({ onSensorAssigned, showToast }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({ isin: '', isvn: '' });
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = async () => {
    setIsSearching(true);
    try {
      const response = await axiosInstance.post('/sensor/search-sensor', {
        isin: formData.isin,
        isvn: formData.isvn,
      });
      console.log('Search Response:', response.data);
      if (response.data.result) {
        setSearchResult([{
          token: response.data.result.token,
          isin: response.data.result.isin,
          isvn: response.data.result.isvn,
          isActive: response.data.result.isActive ? 'Active' : 'Inactive',
        }]);
      } else {
        setSearchResult({ error: response.data.errorResponse?.message || 'Sensor not found' });
      }
    } catch (error) {
      console.error('Error searching sensor:', error);
      setSearchResult({ error: 'Failed to search sensor' });
    } finally {
      setIsSearching(false);
    }
  };

  const handleAssign = async (sensor) => {
    setIsAssigning(true);
    try {
      const response = await axiosInstance.post('/sensor/assign-sensor', {
        isin: sensor.isin,
        isvn: sensor.isvn,
      });
      if (response.data.result) {
        if (onSensorAssigned) await onSensorAssigned();
        if (showToast) showToast('Sensor added successfully');
        setIsOpen(false);
      } else {
        if (showToast) showToast('Failed to assign sensor');
      }
    } catch (error) {
      console.error('Error assigning sensor:', error);
      if (showToast) showToast('Error assigning sensor');
    } finally {
      setIsAssigning(false);
    }
  };

  const tableColumns = [
    { key: 'isin', title: 'ISIN' },
    { key: 'isvn', title: 'ISVN' },
    { key: 'isActive', title: 'Status' },
    {
      key: 'action',
      title: 'Action',
      render: (_, row) => (
        <IconButton
          variant="ghost"
          onClick={() => handleAssign(row)}
          disabled={isAssigning}
        >
          <AiOutlinePlus size={16} />
        </IconButton>
      ),
    },
  ];

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Trigger>
        <Button onClick={() => { setIsOpen(true); setSearchResult(null); setFormData({ isin: '', isvn: '' }); }}>
          Add Sensor
        </Button>
      </Dialog.Trigger>

      <Dialog.Content maxWidth="450px">
        <Dialog.Title>Add Sensor</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Search for a sensor by ISIN and ISVN.
        </Dialog.Description>

        <Flex direction="column" gap="3">
          <label className='w-[100%]'>
            <Text as="div" size="2" mb="1" weight="bold">ISIN</Text>
            <TextField.Root
              value={formData.isin}
              onChange={(e) => handleInputChange('isin', e.target.value)}
              placeholder="Enter ISIN"
            />
          </label>
          <label className='w-[100%]'>
            <Text as="div" size="2" mb="1" weight="bold">ISVN</Text>
            <TextField.Root
              value={formData.isvn}
              onChange={(e) => handleInputChange('isvn', e.target.value)}
              placeholder="Enter ISVN"
            />
          </label>
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Button
            variant="soft"
            color="gray"
            onClick={() => setIsOpen(false)}
            disabled={isSearching || isAssigning}
            type="button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSearch}
            disabled={isSearching || isAssigning || !formData.isin || !formData.isvn}
            type="button"
          >
            {isSearching ? 'Searching...' : 'Search'}
          </Button>
        </Flex>

        {searchResult && (
          <Flex direction="column" gap="2" mt="4" align="center">
            {Array.isArray(searchResult) ? (
              <>
                <Text size="2" color="green">Sensor found</Text>
                <TableComponent
                  dataStructure={searchResult}
                  columns={tableColumns}
                />
              </>
            ) : (
              <Text size="2" color="tomato">{searchResult.error || 'Try again'}</Text>
            )}
          </Flex>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default AddSensorPopup;